import { useState, useCallback, useEffect } from 'react'
import { toast } from 'sonner'
import type { CloserLead, CloserLeadFormData } from '@/types'
import { useAuthStore } from '@/stores/authStore'
import {
  clearCloserLeadDraft,
  emptyCloserLeadFormData,
  hasCloserLeadDraftContent,
  loadCloserLeadDraft,
  saveCloserLeadDraft,
} from '@/utils/closerLeadDraft'
import { useCloserLeadManagerMutations } from '@/hooks/useCloserLeadManagerMutations'
import { normalizeEcommercePlatform } from '@/constants/ecommercePlatforms'
import { getQrLeadScanHints } from '@/utils/qrLeadScanFlow'
import type { ParsedContactQr } from '@/utils/parseContactQrPayload'

import type { UseCloserLeadManagerReturn } from '@/hooks/useCloserLeadManager.types'

export type { UseCloserLeadManagerReturn } from '@/hooks/useCloserLeadManager.types'

export function useCloserLeadManager(onSuccess: () => void, onClose?: () => void): UseCloserLeadManagerReturn {
  const userId = useAuthStore(s => s.user?.id) || ''
  const [showModal, setShowModal] = useState(false)
  const [selectedLead, setSelectedLead] = useState<CloserLead | null>(null)
  const [isNewLead, setIsNewLead] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showRegTentativa, setShowRegTentativa] = useState(false)
  const [saving, setSaving] = useState(false)
  const [backendErrors, setBackendErrors] = useState<Record<string, string>>({})
  const [highlightEmpresa, setHighlightEmpresa] = useState(false)
  const [highlightContato, setHighlightContato] = useState(false)
  const [qrFromWhatsApp, setQrFromWhatsApp] = useState(false)
  const [formData, setFormData] = useState<CloserLeadFormData>(emptyCloserLeadFormData)

  const resetState = useCallback(() => {
    setShowRegTentativa(false)
    setBackendErrors({})
    setSaving(false)
    setHighlightEmpresa(false)
    setHighlightContato(false)
    setQrFromWhatsApp(false)
  }, [])

  const openCreate = useCallback(() => {
    resetState()
    setSelectedLead(null)
    setIsNewLead(true)
    setIsEditing(true)

    const draft = userId ? loadCloserLeadDraft(userId) : null
    if (draft && hasCloserLeadDraftContent(draft)) {
      setFormData(draft)
      toast.info('Rascunho restaurado — continue de onde parou')
    } else {
      setFormData(emptyCloserLeadFormData)
    }

    setShowModal(true)
  }, [resetState, userId])

  const openCreateWithPrefill = useCallback(
    (
      prefill: Partial<CloserLeadFormData>,
      options?: {
        highlightEmpresa?: boolean
        highlightContato?: boolean
        qrFromWhatsApp?: boolean
        qrSource?: ParsedContactQr['source']
      },
    ) => {
      resetState()
      setSelectedLead(null)
      setIsNewLead(true)
      setIsEditing(true)

      const hints = getQrLeadScanHints({
        telefone: prefill.telefone,
        nome: prefill.contato,
        source: options?.qrSource,
      })

      setHighlightEmpresa(
        options?.highlightEmpresa !== undefined ? options.highlightEmpresa : hints.highlightEmpresa,
      )
      setHighlightContato(
        options?.highlightContato !== undefined ? options.highlightContato : hints.highlightContato,
      )
      setQrFromWhatsApp(
        options?.qrFromWhatsApp !== undefined ? options.qrFromWhatsApp : hints.whatsappOnlyPhone,
      )
      setFormData({
        ...emptyCloserLeadFormData,
        ...prefill,
        servicos: prefill.servicos ?? [],
        origem: prefill.origem?.trim() || 'Evento',
      })
      setShowModal(true)
    },
    [resetState],
  )

  const openDetails = useCallback((lead: CloserLead) => {
    resetState()
    setSelectedLead(lead)
    setIsNewLead(false)
    setIsEditing(false)
    setFormData({
      empresa: lead.empresa || '',
      contato: lead.contato || '',
      cargo: lead.cargo || '',
      telefone: lead.telefone || '',
      email: lead.email || '',
      site: lead.site || '',
      segmento: lead.segmento || '',
      plataforma: normalizeEcommercePlatform(lead.plataforma),
      modelo: lead.modelo || 'B2C',
      faturamento: lead.faturamento || '',
      trafego: '',
      origem: lead.origem || '',
      problema: lead.problema || '',
      observacoes: lead.observacoes || '',
      servicos: lead.servicos || [],
      score: lead.score || 50,
      empresaId: lead.empresaId,
      parceiroId: lead.parceiroId,
    })
    setShowModal(true)
  }, [resetState])

  const handleCloseModal = useCallback((options?: { skipDraftSave?: boolean }) => {
    if (options?.skipDraftSave) {
      if (userId) clearCloserLeadDraft(userId)
      setFormData(emptyCloserLeadFormData)
    } else if (isNewLead && userId && hasCloserLeadDraftContent(formData)) {
      saveCloserLeadDraft(userId, formData)
    }
    setShowModal(false)
    onClose?.()
    setTimeout(() => {
      setSelectedLead(null)
      setIsNewLead(false)
      setIsEditing(false)
      setFormData(emptyCloserLeadFormData)
      resetState()
    }, 150)
  }, [resetState, isNewLead, userId, formData, onClose])

  const clearDraft = useCallback(() => {
    if (userId) clearCloserLeadDraft(userId)
    if (isNewLead) setFormData(emptyCloserLeadFormData)
    toast.success('Rascunho descartado')
  }, [userId, isNewLead])

  useEffect(() => {
    if (!showModal || !isNewLead || !userId) return
    const timer = window.setTimeout(() => {
      saveCloserLeadDraft(userId, formData)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [formData, showModal, isNewLead, userId])

  const handleStartEdit = useCallback(() => {
    setIsEditing(true)
    setShowRegTentativa(false)
  }, [])

  const handleCancelEdit = useCallback(() => {
    if (!selectedLead) return
    setBackendErrors({})
    setFormData({
      empresa: selectedLead.empresa || '',
      contato: selectedLead.contato || '',
      cargo: selectedLead.cargo || '',
      telefone: selectedLead.telefone || '',
      email: selectedLead.email || '',
      site: selectedLead.site || '',
      segmento: selectedLead.segmento || '',
      plataforma: normalizeEcommercePlatform(selectedLead.plataforma),
      modelo: selectedLead.modelo || 'B2C',
      faturamento: selectedLead.faturamento || '',
      trafego: '',
      origem: selectedLead.origem || '',
      problema: selectedLead.problema || '',
      observacoes: selectedLead.observacoes || '',
      servicos: selectedLead.servicos || [],
      score: selectedLead.score || 50,
      empresaId: selectedLead.empresaId,
      parceiroId: selectedLead.parceiroId,
    })
    setIsEditing(false)
  }, [selectedLead])

  const clearDraftForUser = useCallback(() => {
    if (userId) clearCloserLeadDraft(userId)
  }, [userId])

  const mutations = useCloserLeadManagerMutations({
    selectedLead,
    userId,
    onSuccess,
    handleCloseModal,
    setSaving,
    setBackendErrors,
    clearDraftForUser,
    setHighlightEmpresa,
    setHighlightContato,
  })

  const hasDraft = isNewLead && hasCloserLeadDraftContent(formData)

  return {
    showModal,
    selectedLead,
    isNewLead,
    isEditing,
    showRegTentativa,
    saving,
    backendErrors,
    formData,
    hasDraft,
    setFormData,
    openCreate,
    openCreateWithPrefill,
    openDetails,
    handleCloseModal,
    handleStartEdit,
    handleCancelEdit,
    ...mutations,
    clearDraft,
    setShowRegTentativa,
    highlightEmpresa,
    setHighlightEmpresa,
    highlightContato,
    setHighlightContato,
    qrFromWhatsApp,
    setQrFromWhatsApp,
  }
}
